import React from 'react';
import { observer } from 'mobx-react';
import { routerStore } from 'core/routing';

import { analytics } from 'core/analytics';
import { frameStore } from 'apps/components/Frame/appManager';
import { useChatControl } from 'libs/rendering/hooks';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { getFormatObject } from 'libs/rendering/utils';
import { clientStore } from 'apps/client/app';

import type { Space as Workspace } from 'generated/typescript';
import { MakeButtonStyled } from './headerFileFeatures.styled';

export const MakeButtonContainer = observer(() => {
  const { file, sharedFileManager } = frameStore;
  const chatControl = useChatControl();
  const routeName = routerStore.route.name;
  const viewerControl = useViewerControl();
  const routeParams = routerStore.route.params;

  if (!file) return null;
  // In these routes this is just an Info button to show toggle the UI, no need to check
  // properties of whether the file can be Made.
  if (!frameStore.isRouteWithInfoButton) {
    const { formatObject, extension } = getFormatObject(file);

    if (formatObject.viewer === 'GltfViewer') {
      if (!viewerControl.assemblyReady) return null;
    } else if (extension !== 'pdf') return null;
  }

  // If we are in Make button mode, hide it in some cases
  if (!frameStore.isRouteWithInfoButton) {
    // The follow-up code should override this one anyway
    if (
      sharedFileManager?.sharedFile &&
      // Hide button for limited-collaborators (canUpdate shows permission access)
      !sharedFileManager.sharedFile.canUpdate
    ) {
      return null;
    }

    if (!file.space) {
      return null;
    }

    const fileParentSpaceId = file.space.parent.id;
    const isFileParentSpaceInOurWorkspaces = clientStore.workspaces.some(
      (workspace: Workspace) => workspace.id === fileParentSpaceId,
    );
    if (file.private && !isFileParentSpaceInOurWorkspaces) return null;
  }

  const isActive = frameStore.canShowRightPanel && !chatControl.chatVisible;

  let text = 'Make';
  if (frameStore.isRouteWithInfoButton) text = 'Info';

  const handleClick = () => {
    let panelParamValue = '';
    if (frameStore.isRouteWithInfoButton) panelParamValue = 'info';
    else if (frameStore.isRouteWithMakeButton) panelParamValue = 'rfq';

    const newParams = { ...routeParams };
    if (newParams.panel === panelParamValue) delete newParams.panel;
    else newParams.panel = panelParamValue;
    routerStore.navigate(routeName, newParams, { replace: true });
  };

  return (
    <MakeButtonStyled
      onClick={() => {
        // @ts-ignore
        analytics.sendEvent('make-part-click-cad-rooms', {}, { ga: true });
        handleClick();
      }}
      icon='factory'
      iconPosition='right'
      text={text}
      active={isActive}
      appearance='plain'
      removeBold
    />
  );
});
